<template>
  <figure :class="['game-icon', `game-icon--${tier}`, `game-icon--${kind}`, { 'game-icon--empty': !src || broken }]">
    <div class="game-icon__frame">
      <img v-if="src && !broken" :src="src" :alt="alt" decoding="async" @error="broken = true">
      <span v-else aria-hidden="true">{{ fallback }}</span>
    </div>
    <figcaption v-if="label">{{ label }}</figcaption>
  </figure>
</template>

<script>
export default {
  name: 'GameIcon',
  props: {
    src: { type: String, default: '' },
    alt: { type: String, default: '' },
    label: { type: String, default: '' },
    rarity: { type: Number, default: 0 },
    kind: { type: String, default: 'item' }
  },
  data () {
    return { broken: false }
  },
  computed: {
    tier () {
      if (this.kind === 'power') return 'power'
      if (this.rarity === 11) return 'limited'
      if (this.rarity === 8) return 'event'
      if (this.rarity === 5) return 'visceral'
      if (this.rarity === 4) return 'very-rare'
      if (this.rarity === 3) return 'rare'
      if (this.rarity === 2) return 'uncommon'
      return 'common'
    },
    fallback () {
      const words = this.alt.replace(/[^a-z0-9 ]/gi, ' ').trim().split(/\s+/).filter(Boolean)
      return words.slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase() || '?'
    }
  },
  watch: {
    src () {
      this.broken = false
    }
  }
}
</script>

<style lang="scss" scoped>
.game-icon {
  --rarity: #76513c;
  --rarity-bright: #b88259;
  width: 82px;
  min-width: 82px;
  margin: 0;
  display: grid;
  justify-items: center;
  position: relative;
}
.game-icon__frame {
  width: 78px;
  height: 78px;
  border: 1px solid color-mix(in srgb, var(--rarity-bright) 70%, white 8%);
  border-radius: 18px;
  background:
    linear-gradient(145deg, color-mix(in srgb, var(--rarity-bright) 24%, transparent), transparent 56%),
    radial-gradient(circle at 50% 42%, color-mix(in srgb, var(--rarity) 74%, #111 26%), color-mix(in srgb, var(--rarity) 36%, #090b0d 64%) 68%);
  box-shadow: inset 0 0 22px rgba(0,0,0,.32), 0 8px 24px color-mix(in srgb, var(--rarity) 20%, transparent);
  display: grid;
  place-items: center;
  overflow: hidden;
  position: relative;
}
.game-icon__frame::before {
  content: '';
  width: 52px;
  height: 52px;
  border: 1px solid color-mix(in srgb, var(--rarity-bright) 48%, transparent);
  background: rgba(255,255,255,.035);
  transform: rotate(45deg);
  position: absolute;
}
.game-icon img {
  width: 88%;
  height: 88%;
  object-fit: contain;
  filter: drop-shadow(0 5px 7px rgba(0,0,0,.6));
  position: relative;
  z-index: 1;
}
.game-icon__frame > span {
  color: rgba(255,255,255,.82);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -.04em;
  position: relative;
  z-index: 1;
}
.game-icon figcaption {
  max-width: 82px;
  margin-top: 5px;
  color: color-mix(in srgb, var(--rarity-bright) 76%, white 24%);
  font-size: 7px;
  font-weight: 900;
  letter-spacing: .08em;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}
.game-icon--common { --rarity: #5d4533; --rarity-bright: #ab713c; }
.game-icon--uncommon { --rarity: #0f5f14; --rarity-bright: #199b1e; }
.game-icon--rare { --rarity: #174c6b; --rarity-bright: #2f9cd6; }
.game-icon--very-rare { --rarity: #672d7f; --rarity-bright: #ac3ee3; }
.game-icon--visceral { --rarity: #cf0b45; --rarity-bright: #ff0955; }
.game-icon--event { --rarity: #8a5d00; --rarity-bright: #ffa800; }
.game-icon--limited { --rarity: #74341f; --rarity-bright: #c2593a; }
.game-icon--power { --rarity: #164f66; --rarity-bright: #68dcff; }
.game-icon--empty .game-icon__frame { border-style: dashed; }
</style>
